import { IGenericModal, Modal } from 'components/Modal/Modal';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  buildPriorityLevelPayload,
  PriorityLevelForm,
  priorityLevelToFormDefaults
} from '../PriorityLevelForm/PriorityLevelForm';
import { IPriorityLevel, IPriorityLevelFormFields } from 'types/priorityLevel';
import {
  deletePriorityLevel,
  editPriorityLevel,
  getPriorityLevel
} from 'api/priorityLevels';
import { toast } from 'react-toastify';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'components/_form';
import { yupResolver } from '@hookform/resolvers/yup';
import { priorityLevelEditSchema } from 'validation/priorityLevelSchemas';
import { useAreasQuery } from 'hooks/queries/useAreasQuery';
import { useMainCompanyQuery } from 'hooks/queries/useMainCompanyQuery';
import { IOption } from 'components/_form/NestedSelect/NestedSelect';
import {
  expandSelectedAreaIdsWithAncestors,
  findOptionByValueInTree
} from 'components/_form/NestedSelect/utilities';
import {
  parentToChildrenAreasStructure,
  parseParentToChildrenAreasStructureToOptions
} from 'utilities/areas';

interface Props extends IGenericModal {
  onSubmitSuccess?: (priorityLevel: IPriorityLevel) => void;
  onDeleteSuccess?: () => void;
  editingPriorityLevelId: number;
}

export const EditPriorityLevelForm = ({
  isOpen,
  onCancelClick,
  onSubmitSuccess,
  onDeleteSuccess,
  editingPriorityLevelId
}: Props) => {
  const [editingPriorityLevel, setEditingPriorityLevel] =
    useState<IPriorityLevel>();
  const { data: mainCompany } = useMainCompanyQuery();

  const { t } = useTranslation();

  const methods = useForm<IPriorityLevelFormFields & { active?: boolean }>({
    mode: 'onChange',
    resolver: yupResolver(priorityLevelEditSchema)
  });

  const { data: areasData = [] } = useAreasQuery();
  const areaTreeRoots = useMemo(
    () =>
      parseParentToChildrenAreasStructureToOptions(
        parentToChildrenAreasStructure(areasData)
      ),
    [areasData]
  );

  const { handleSubmit, reset } = methods;

  const handleSubmitPriorityLevelForm = async (
    data: IPriorityLevelFormFields & { active?: boolean }
  ) => {
    const areaIdsForApi = expandSelectedAreaIdsWithAncestors(
      areaTreeRoots,
      data.areas ?? []
    );

    const priorityLevelRes = await editPriorityLevel(editingPriorityLevelId, {
      priority_level: buildPriorityLevelPayload(data, areaIdsForApi, mainCompany)
    });

    if ('error' in priorityLevelRes) {
      toast.error(priorityLevelRes.error.message);
      return;
    }

    onSubmitSuccess?.(priorityLevelRes.data);
  };

  const handleDeletePriorityLevel = async () => {
    const deleteRes = await deletePriorityLevel(editingPriorityLevelId);

    if (deleteRes && 'error' in deleteRes) {
      toast.error(deleteRes.error.message);
      return;
    }

    onDeleteSuccess?.();
  };

  const handleGetPriorityLevel = useCallback(async () => {
    const priorityLevelRes = await getPriorityLevel(editingPriorityLevelId);

    if ('error' in priorityLevelRes) {
      return;
    }

    setEditingPriorityLevel(priorityLevelRes.data);
  }, [editingPriorityLevelId]);

  const handleSetDefaultValues = useCallback(() => {
    if (!editingPriorityLevel) {
      return;
    }

    reset({
      ...priorityLevelToFormDefaults(editingPriorityLevel, mainCompany),
      areas:
        editingPriorityLevel.areas
          ?.map(({ id }) => findOptionByValueInTree(areaTreeRoots, String(id)))
          .filter((o): o is IOption => !!o) ?? []
    });
  }, [editingPriorityLevel, reset, areaTreeRoots, mainCompany]);

  useEffect(() => {
    handleGetPriorityLevel();
  }, [handleGetPriorityLevel]);

  useEffect(() => {
    handleSetDefaultValues();
  }, [handleSetDefaultValues]);

  return (
    <Modal
      isOpen={isOpen}
      onCancelClick={() => {
        onCancelClick();
      }}
      headerTitle={t('administrationTasksView.editPriority')}
      mainButton={{
        action: handleSubmit((data) => {
          handleSubmitPriorityLevelForm(data);
        }),
        variant: 'eucalyptus',
        label: t('buttons.save')
      }}
      isSecondButtonVisible
      gridTemplateColumns="70%"
      additionalFooter={
        <Button variant="red" onClick={handleDeletePriorityLevel}>
          {t('buttons.delete')}
        </Button>
      }
    >
      <FormProvider {...methods}>
        <form>
          <PriorityLevelForm />
        </form>
      </FormProvider>
    </Modal>
  );
};
